import { useEffect, useState } from "react";
import { Form, Table } from "react-bootstrap";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { DefaultSelected, SimpleModal } from "src/app/components";
import { useCheckIfKeywordsAreBoosted } from "src/app/hooks";
import { OrderTransport } from "src/app/transports/order";

export interface IAddBoostToOrderModelContainer {
    selectedRows: Array<TSelectedRows>;
    handlerValueToUpdateGrid(update: boolean): void;
    handleClose(): void;
    showModal: boolean;
}
interface ISelectedRows {
    id: number;
    keyword?: string;
    keywords?: string[];
    url: string;
}

export type TSelectedRows =
    | (ISelectedRows & { keyword: string; keywords?: never })
    | (ISelectedRows & { keyword?: never; keywords: string[] });

const AddBoostToOrderModelContainer = (props: IAddBoostToOrderModelContainer) => {
    const { selectedRows, handlerValueToUpdateGrid, handleClose, showModal } = props;

    const { formatMessage } = useIntl();

    const [loading, setLoading] = useState(false);
    const [selectedKeywords, setSelectedKeywords] = useState<
        Array<{ orderId: number; keyword: string }>
    >([]);

    const {
        boostedKeywords,
        loading: boostedKeywordsLoading,
        refresh,
    } = useCheckIfKeywordsAreBoosted();

    useEffect(() => {
        if (showModal && selectedRows) {
            const keywordSelected: Array<{ orderId: number; keyword: string }> = [];
            if (selectedRows[0].keywords) {
                selectedRows.forEach((el) => {
                    if (el.keywords)
                        keywordSelected.push({ orderId: el.id, keyword: el.keywords[0] });
                });
            } else {
                selectedRows.forEach((el) => {
                    if (el.keyword) keywordSelected.push({ orderId: el.id, keyword: el.keyword });
                });
                setSelectedKeywords(keywordSelected);
            }
            setSelectedKeywords(keywordSelected);
        }
    }, [showModal]);

    useEffect(() => {
        if (selectedKeywords.length === 0) return;

        refresh(selectedKeywords);
    }, [selectedKeywords]);

    const onChangeInput = (selectedRow: ISelectedRows, value: string) => {
        setSelectedKeywords((currentValues) => {
            const auxSelectedArray = [...currentValues];
            const currentValueIndex = auxSelectedArray.findIndex(
                (toFind) => toFind.orderId === selectedRow.id
            );

            if (currentValueIndex !== -1) {
                auxSelectedArray[currentValueIndex] = {
                    orderId: selectedRow.id,
                    keyword: value,
                };
            } else {
                auxSelectedArray.push({
                    orderId: selectedRow.id,
                    keyword: value,
                });
            }
            return auxSelectedArray;
        });
    };

    const onSubmit = async () => {
        setLoading(true);

        if (!selectedKeywords) return;

        try {
            await OrderTransport.addBoostToOrders(selectedKeywords);
            toast.success(formatMessage({ id: "common.notification.success" }));
            handlerValueToUpdateGrid(true);
            handleClose();
        } catch (error: any) {
            toast.error(
                error?.response?.data?.errors[0]?.message ||
                    formatMessage({ id: "common.notification.error" })
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <SimpleModal
            entityName="Boost"
            showModal={showModal}
            handleClose={handleClose}
            buttonConfirmationAction={onSubmit}
            buttonConfirmationLabel={formatMessage({ id: "common.action.confirmation" })}
            formId=""
            loading={loading || boostedKeywordsLoading}
            modalProps={{ size: "xl" }}
        >
            <>
                <Form.Label>
                    {formatMessage({ id: "common.field.changeOrderType.label" })}
                </Form.Label>
                <Table className="table-center-td" bordered striped>
                    <thead>
                        <tr>
                            <th>Keyword</th>
                            <th>Url</th>
                        </tr>
                    </thead>
                    <tbody>
                        {selectedKeywords &&
                            selectedRows.map((el, index) => {
                                return (
                                    <tr
                                        key={index}
                                        className={
                                            boostedKeywords?.findIndex((value) => {
                                                const selectedKeyword = selectedKeywords.find(
                                                    (sk) =>
                                                        sk.orderId === el.id &&
                                                        sk.keyword === value.keyword
                                                );
                                                return (
                                                    (value.orderId === el.id &&
                                                        value.keyword === el.keyword) ||
                                                    (selectedKeyword &&
                                                        selectedKeyword.orderId === value.orderId &&
                                                        selectedKeyword.keyword === value.keyword)
                                                );
                                            }) !== -1
                                                ? "bg-danger"
                                                : ""
                                        }
                                    >
                                        <td>
                                            {el.keyword || el.keywords ? (
                                                el.keyword ? (
                                                    el.keyword || "n/d"
                                                ) : (
                                                    <DefaultSelected
                                                        value={
                                                            selectedKeywords.find(
                                                                (kw) => kw.orderId === el.id
                                                            )?.keyword
                                                        }
                                                        options={el.keywords?.map((keys) => ({
                                                            value: keys,
                                                            label: keys,
                                                        }))}
                                                        onChange={(value) => {
                                                            if (typeof value !== "string") return;
                                                            onChangeInput(el, value);
                                                        }}
                                                    />
                                                )
                                            ) : (
                                                <input
                                                    onChange={(event) =>
                                                        onChangeInput(el, event.currentTarget.value)
                                                    }
                                                />
                                            )}
                                        </td>
                                        <td>
                                            <a href={el.url} target="_blank">
                                                {el.url}
                                            </a>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </Table>
            </>
        </SimpleModal>
    );
};

export default AddBoostToOrderModelContainer;
